// imperial/groceryUtilsImperial.js - Imperial measurement system utilities
import { normalizeIngredientName, getDensity } from '../groceryUtils';

// Define imperial-specific unit conversions
export const IMPERIAL_UNIT_CONVERSIONS = {
  weight: {
    baseUnit: "oz",
    conversions: {
      lb: 16,
      oz: 1
    }
  },
  volume: {
    baseUnit: "fl oz",
    conversions: {
      gal: 128,
      qt: 32,
      pint: 16,
      cup: 8,
      "fl oz": 1,
      tbsp: 0.5,
      tsp: 0.167
    }
  },
  count: {
    baseUnit: "piece",
    conversions: {
      piece: 1,
      pieces: 1,
      slice: 1,
      slices: 1,
      unit: 1,
      units: 1,
      whole: 1,
      bunch: 1,
      clove: 1,
      cloves: 1
    }
  }
};

/**
 * Convert unit from metric to imperial
 * @param {number} value - Quantity value
 * @param {string} unit - Metric unit
 * @returns {Object} Converted value and unit in imperial
 */
export const metricToImperial = (value, unit) => {
  if (!value || !unit) return { value, unit };
  
  const unitLower = unit.toLowerCase();
  
  // Weight conversions
  if (unitLower === 'g') return { value: value / 28.35, unit: 'oz' };
  if (unitLower === 'kg') return { value: value / 0.45359, unit: 'lb' };
  
  // Volume conversions
  if (unitLower === 'ml') return { value: value / 29.57, unit: 'fl oz' };
  if (unitLower === 'l') return { value: value / 0.9464, unit: 'qt' };
  
  // If not recognized, keep as is
  return { value, unit };
};

/**
 * Normalize an ingredient quantity to base units in imperial system
 * @param {number} value - Quantity value
 * @param {string} unit - Unit of measurement
 * @param {string} ingredientName - Name of ingredient (for density conversions)
 * @returns {Object} Normalized value and unit in imperial base units
 */
export const normalizeToImperialBase = (value, unit, ingredientName = '') => {
  if (!unit) return { value, unit: 'piece', measurementType: 'count' };
  
  const unitLower = unit.toLowerCase().trim();
  
  // First check if it's a metric unit that needs conversion
  const imperialConverted = metricToImperial(value, unitLower);
  if (imperialConverted.unit !== unitLower) {
    return normalizeToImperialBase(imperialConverted.value, imperialConverted.unit, ingredientName);
  }
  
  // Handle each measurement type
  // Weight units
  if (IMPERIAL_UNIT_CONVERSIONS.weight.conversions[unitLower]) {
    const factor = IMPERIAL_UNIT_CONVERSIONS.weight.conversions[unitLower];
    return { 
      value: value * factor, 
      unit: 'oz', 
      measurementType: 'weight', 
      originalUnit: unit 
    };
  }
  
  // Volume units
  if (IMPERIAL_UNIT_CONVERSIONS.volume.conversions[unitLower]) {
    const factor = IMPERIAL_UNIT_CONVERSIONS.volume.conversions[unitLower];
    return { 
      value: value * factor, 
      unit: 'fl oz', 
      measurementType: 'volume', 
      originalUnit: unit 
    };
  }
  
  // Count units
  if (IMPERIAL_UNIT_CONVERSIONS.count.conversions[unitLower]) {
    return { 
      value, 
      unit: 'piece', 
      measurementType: 'count', 
      originalUnit: unit 
    };
  }
  
  // Default handling if unrecognized
  console.warn(`Unrecognized unit: ${unit}, defaulting to piece`);
  return { value, unit: 'piece', measurementType: 'count', originalUnit: unit };
};

/**
 * Convert between volume and weight units in imperial system
 * @param {number} value - Quantity value
 * @param {string} fromUnit - Source unit (fl oz or oz)
 * @param {string} toUnit - Target unit (fl oz or oz)
 * @param {string} ingredientName - Ingredient name for density lookup
 * @returns {number|null} Converted value or null if conversion not possible
 */
export const convertVolumeWeightImperial = (value, fromUnit, toUnit, ingredientName) => {
  if (fromUnit === toUnit) return value;
  
  // Only handle oz <-> fl oz conversions
  if ((fromUnit === 'oz' && toUnit === 'fl oz') || (fromUnit === 'fl oz' && toUnit === 'oz')) {
    // Get density from PRODUCT_METADATA using the utility function
    const metricDensity = getDensity(ingredientName);
    
    if (!metricDensity) {
      console.warn(`No density information for ${ingredientName}, cannot convert between weight and volume`);
      return null;
    }
    
    // Convert density from g/ml to oz/fl oz (1 g/ml ≈ 1.043 oz/fl oz)
    const ozPerFlOz = metricDensity * 1.043;
    
    // oz to fl oz: divide by density
    if (fromUnit === 'oz' && toUnit === 'fl oz') {
      return value / ozPerFlOz;
    }
    
    // fl oz to oz: multiply by density
    if (fromUnit === 'fl oz' && toUnit === 'oz') {
      return value * ozPerFlOz;
    }
  }
  
  console.warn(`Cannot convert between ${fromUnit} and ${toUnit} in imperial system`);
  return null;
};

/**
 * Find the best package size to purchase (imperial)
 * @param {Array} standardSizes - Available package sizes
 * @param {number} requiredAmount - Required amount in base units
 * @param {string} baseUnit - Base unit (oz, fl oz, piece)
 * @returns {Object} Best package option
 */
export const pickBestPackageImperial = (standardSizes, requiredAmount, baseUnit) => {
  if (!Array.isArray(standardSizes) || standardSizes.length === 0) {
    throw new Error('No standard sizes available');
  }
  
  // Sort sizes by amount
  const sortedSizes = [...standardSizes].sort((a, b) => {
    // Convert all to base unit if needed
    const aAmount = normalizeToImperialBase(a.amount, a.unit).value;
    const bAmount = normalizeToImperialBase(b.amount, b.unit).value;
    return aAmount - bAmount;
  });
  
  // Find first package that meets or exceeds requirement
  const sufficientPackage = sortedSizes.find(pkg => {
    const pkgAmount = normalizeToImperialBase(pkg.amount, pkg.unit).value;
    return pkgAmount >= requiredAmount;
  });
  
  // If found a sufficient package, return it
  if (sufficientPackage) return sufficientPackage;
  
  // Otherwise return the largest available package
  return sortedSizes[sortedSizes.length - 1];
};

/**
 * Calculate total cost with proper formatting
 * @param {Array} groceryList - List of grocery items
 * @param {String} currencySymbol - Currency symbol to use
 * @returns {String} Formatted total cost
 */
export const calculateTotalCostImperial = (groceryList, currencySymbol) => {
  const total = groceryList.reduce((sum, item) => {
    if (!item.totalPrice) return sum;
    
    const priceMatch = String(item.totalPrice).match(/(\d+(\.\d+)?)/);
    if (priceMatch) {
      return sum + parseFloat(priceMatch[1]);
    }
    return sum;
  }, 0);
  
  return `${currencySymbol}${total.toFixed(2)}`;
};

/**
 * Organize products by category for imperial system
 * @param {Array} groceryList - Grocery list items
 * @returns {Array} Categorized product groups
 */
export const categorizeProductsImperial = (groceryList) => {
  const categories = {};
  
  groceryList.forEach(item => {
    const category = item.category || 'Other';
    if (!categories[category]) {
      categories[category] = []; 
    }
    categories[category].push(item);
  });
  
  return Object.entries(categories).map(([category, items]) => ({
    category,
    items
  }));
};